"use client";

import { Copy } from "iconsax-reactjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckIcon, type CheckIconHandle } from "@/components/ui/CheckIcon";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export const WalletAddress = ({
  address,
  size = 16,
  truncateSize = "md",
  className,
  canCopy = true,
  responsive = true,
  addressClassName,
  buttonClassName,
  checkIconClassName,
  copyIconClassName,
}: {
  address?: string | `0x${string}`;
  size?: number;
  truncateSize?: "xs" | "sm" | "md" | "lg" | "full";
  className?: string;
  canCopy?: boolean;
  responsive?: boolean;
  addressClassName?: string;
  buttonClassName?: string;
  checkIconClassName?: string;
  copyIconClassName?: string;
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [responsiveTruncateSize, setResponsiveTruncateSize] = useState<
    "xs" | "sm" | "md" | "lg" | "full"
  >("md");
  const checkIconRef = useRef<CheckIconHandle | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleCopy = useCallback(() => {
    if (address) {
      // Clear any existing text selection to prevent copying selected text instead
      window.getSelection()?.removeAllRanges();

      setIsCopied(true);
      setTimeout(() => {
        checkIconRef.current?.startAnimation();
      }, 0);

      setTimeout(() => setIsCopied(false), 3000);
      navigator.clipboard.writeText(address.trim());
    }
  }, [address]);

  // Responsive truncation logic
  useEffect(() => {
    if (!responsive || !containerRef.current || !address) return;

    const updateTruncateSize = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.offsetWidth;
      const copyButtonWidth = 32; // Approximate width of copy button
      const padding = 16; // Approximate padding
      const availableWidth = containerWidth - copyButtonWidth - padding;

      // Calculate approximate character width (monospace assumption)
      const charWidth = 8; // Approximate character width in pixels
      const addressLength = address.length;
      const fullAddressWidth = addressLength * charWidth;

      if (fullAddressWidth <= availableWidth) {
        setResponsiveTruncateSize("full");
      } else {
        // Calculate how many characters we can fit
        const maxChars = Math.floor(availableWidth / charWidth);

        if (maxChars >= 24) {
          setResponsiveTruncateSize("lg");
        } else if (maxChars >= 20) {
          setResponsiveTruncateSize("md");
        } else if (maxChars >= 12) {
          setResponsiveTruncateSize("sm");
        } else {
          setResponsiveTruncateSize("xs");
        }
      }
    };

    // Initial calculation
    updateTruncateSize();

    // Set up ResizeObserver
    const resizeObserver = new ResizeObserver(updateTruncateSize);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [responsive, address]);

  if (!address) return <Skeleton className="w-20 h-4" />;

  const currentTruncateSize = responsive
    ? responsiveTruncateSize
    : truncateSize;

  const firstSlice =
    currentTruncateSize === "full"
      ? 0
      : currentTruncateSize === "lg"
        ? 10
        : currentTruncateSize === "sm"
          ? 5
          : currentTruncateSize === "xs"
            ? 6
            : 10; // default for "md"
  const secondSlice =
    currentTruncateSize === "full"
      ? 0
      : currentTruncateSize === "lg"
        ? 7
        : currentTruncateSize === "sm"
          ? 4
          : currentTruncateSize === "xs"
            ? 4
            : 7; // default for "md"

  const truncatedAddress =
    currentTruncateSize === "full"
      ? address
      : `${address.slice(0, firstSlice)}...${address.slice(-secondSlice)}`;

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex min-w-0 max-w-full items-center justify-between gap-2 overflow-hidden font-inter",
        className,
      )}
    >
      <p
        className={cn(
          "min-w-0 flex-1 truncate text-inherit mt-1",
          addressClassName,
        )}
      >
        {truncatedAddress}
      </p>
      {canCopy ? (
        <>
          {isCopied ? (
            <Button
              size={"icon"}
              className={cn("shrink-0 p-0", buttonClassName)}
              variant={"light"}
              type="button"
            >
              <CheckIcon
                className={cn("text-green-500", checkIconClassName)}
                ref={checkIconRef}
                size={size}
              />
            </Button>
          ) : (
            <Button
              size={"icon"}
              className={cn("shrink-0 p-0 text-inherit", buttonClassName)}
              variant={"light"}
              onClick={handleCopy}
              type="button"
            >
              <Copy size={size} className={copyIconClassName} />
            </Button>
          )}
        </>
      ) : null}
    </div>
  );
};
